import { useEffect, useState } from "react";
import { FileDown, Save, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiErrorMessage } from "@/lib/api";

const FIELDS = [
  {
    key: "rekening_sumber",
    label: "Rekening Sumber",
    placeholder: "1240007558910",
    hint: "Nomor rekening pengirim (perusahaan) — tampil di Col C baris header.",
  },
  {
    key: "beneficiary_address",
    label: "Beneficiary Address",
    placeholder: "JAKARTA",
    hint: "Alamat penerima default — tampil di Col C setiap baris data.",
  },
  {
    key: "transfer_currency",
    label: "Transfer Currency",
    placeholder: "IDR",
    hint: "Kode mata uang transfer — tampil di Col F. Default IDR.",
  },
  {
    key: "benef_bank_address",
    label: "Benef Bank Address 1",
    placeholder: "Jakarta",
    hint: "Alamat bank penerima default — tampil di Col M.",
  },
  {
    key: "email_notifikasi",
    label: "Email Notifikasi",
    placeholder: "notif@perusahaan.co.id",
    hint: "Alamat email untuk notifikasi bank — tampil di Col R.",
  },
];

export function PayrollCsvKonfigSection() {
  const [values, setValues] = useState({
    rekening_sumber: "",
    beneficiary_address: "",
    transfer_currency: "IDR",
    benef_bank_address: "",
    email_notifikasi: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/payroll-csv");
      setValues({
        rekening_sumber: data.rekening_sumber || "",
        beneficiary_address: data.beneficiary_address || "",
        transfer_currency: data.transfer_currency || "IDR",
        benef_bank_address: data.benef_bank_address || "",
        email_notifikasi: data.email_notifikasi || "",
      });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.put("/settings/payroll-csv", {
        rekening_sumber: values.rekening_sumber.trim(),
        beneficiary_address: values.beneficiary_address.trim(),
        transfer_currency: (values.transfer_currency || "IDR").trim(),
        benef_bank_address: values.benef_bank_address.trim(),
        email_notifikasi: values.email_notifikasi.trim(),
      });
      toast.success("Pengaturan CSV Payroll disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="section-payroll-csv">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700 shrink-0">
          <FileDown size={18} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-lg font-extrabold text-slate-900 leading-tight">
            Pengaturan CSV Payroll (Format Mandiri)
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Data statis untuk file <b>CSV Transfer Bank</b> yang diunduh dari menu <b>Laporan Gaji</b>.
            Nilai-nilai ini otomatis diisikan pada kolom-kolom tetap CSV (Rekening Sumber, alamat
            penerima, mata uang, alamat bank, dan email notifikasi).
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 text-center text-slate-500 py-6 text-sm">Memuat pengaturan…</div>
      ) : (
        <>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.key === "email_notifikasi" ? "sm:col-span-2" : ""}>
                <Label className="text-xs font-bold text-slate-600">{f.label}</Label>
                <Input
                  value={values[f.key]}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="mt-1.5"
                  data-testid={`input-csv-${f.key}`}
                />
                <p className="text-[11px] text-slate-500 mt-1">{f.hint}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-md border border-teal-100 bg-teal-50/60 p-3 flex items-start gap-2 text-[11px] text-teal-900">
            <Info size={13} className="mt-0.5 shrink-0" />
            <div>
              Berita Payroll (Col H) diisi <b>manual</b> saat unduh CSV di Laporan Gaji, dan Customer
              Reference (Col I) otomatis <span className="font-mono-clock">REFF 0001, 0002, …</span>.
              Kolom <b>IBU/LBU</b> ditentukan otomatis: <b>IBU</b> bila bank penerima Mandiri, selainnya <b>LBU</b>.
            </div>
          </div>

          <div className="mt-4">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800 gap-1.5"
              data-testid="save-csv-settings"
            >
              <Save size={13} /> {saving ? "…" : "Simpan"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default PayrollCsvKonfigSection;
